/**
 * DemoDriver — generates fake events to animate the pixel world for Phase 1 demo.
 *
 * Cycles agents through states and sends messages between random pairs.
 */

import type { AgentStatus, EdgeType } from "./PixelWorldStore.js";

interface DemoStore {
  setAgentStatus: (agentId: string, status: AgentStatus) => void;
  enqueueMessage: (msg: { fromAgentId: string; toAgentId: string; edgeType: EdgeType }) => void;
}

const EDGE_TYPES: EdgeType[] = ["delegates-to", "reports-to", "peers-with", "pipes-to"];

const STATUS_CYCLE: AgentStatus[] = ["idle", "running", "running", "running", "waiting", "running", "idle"];

export class DemoDriver {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private agentIds: string[] = [];
  private store: DemoStore | null = null;
  private tick = 0;
  private agentPhase = new Map<string, number>();

  start(store: DemoStore, agentIds: string[]): void {
    this.store = store;
    this.agentIds = agentIds;
    this.tick = 0;

    // Initialize: all agents start idle
    for (const id of agentIds) {
      store.setAgentStatus(id, "idle");
      this.agentPhase.set(id, 0);
    }

    // Main loop — tick every 1.5s
    this.intervalId = setInterval(() => this.doTick(), 1500);

    // Start the first agent running after a short delay
    setTimeout(() => {
      if (this.store && agentIds.length > 0) {
        this.store.setAgentStatus(agentIds[0], "running");
        this.agentPhase.set(agentIds[0], 1);
      }
    }, 500);
  }

  stop(): void {
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.store = null;
  }

  private doTick(): void {
    if (!this.store || this.agentIds.length === 0) return;
    this.tick++;

    // Advance one random agent's status phase
    const agentIdx = this.tick % this.agentIds.length;
    const agentId = this.agentIds[agentIdx];
    const currentPhase = this.agentPhase.get(agentId) ?? 0;
    const nextPhase = (currentPhase + 1) % STATUS_CYCLE.length;
    this.agentPhase.set(agentId, nextPhase);

    let status = STATUS_CYCLE[nextPhase];

    // Occasionally trigger an error (every 15th tick on the 2nd agent)
    if (this.tick % 15 === 0 && this.agentIds.length > 1) {
      const errorAgent = this.agentIds[1];
      this.store.setAgentStatus(errorAgent, "error");
      // Auto-recover after a few ticks
      setTimeout(() => {
        this.store?.setAgentStatus(errorAgent, "idle");
      }, 3000);
      return;
    }

    this.store.setAgentStatus(agentId, status);

    // Send a message between two agents every 2nd tick
    if (this.tick % 2 === 0 && this.agentIds.length >= 2) {
      const fromIdx = this.tick % this.agentIds.length;
      const toIdx = (fromIdx + 1 + Math.floor(this.tick / 3)) % this.agentIds.length;
      if (fromIdx !== toIdx) {
        this.store.enqueueMessage({
          fromAgentId: this.agentIds[fromIdx],
          toAgentId: this.agentIds[toIdx],
          edgeType: EDGE_TYPES[this.tick % EDGE_TYPES.length],
        });
      }
    }
  }
}
